import { forwardRef, useState, type ComponentProps, type ReactNode } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectPlainTrigger } from '@/components/ui/select';
import { MonthPickerPanel } from '@/components/MonthPicker';
import { formatMonthKey } from '@/lib/months';
import { cn } from '@/lib/utils';

/** Small sliders button next to a column label; blue while something is chosen. */
export const FilterIconButton = forwardRef<HTMLButtonElement, ComponentProps<'button'> & { active: boolean; label: string }>(
  ({ active, label, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label={`Filter by ${label}`}
      className={cn(
        'flex h-6 w-6 items-center justify-center rounded-md transition-colors',
        active ? 'bg-[#4369a0]/10 text-[#4369a0] hover:bg-[#4369a0]/15' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700',
        className
      )}
      {...props}
    >
      <SlidersHorizontal className="h-3.5 w-3.5" />
    </button>
  )
);
FilterIconButton.displayName = 'FilterIconButton';

interface FilterHeaderProps {
  label: string;
  /** The filter button (it opens the menu or picker). */
  control: ReactNode;
  /** What is chosen, shown under the label; empty when nothing is chosen. */
  selection?: string;
  onClear: () => void;
}

/** Column label with its filter button, and the chosen value under it with a ✕ to clear. */
export function FilterHeader({ label, control, selection, onClear }: FilterHeaderProps) {
  return (
    <div className="flex flex-col py-1">
      <div className="flex items-center gap-0.5">
        <span className="text-xs font-semibold tracking-wide text-gray-800">{label}</span>
        {control}
      </div>
      {selection && (
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-medium text-[#4369a0]">
          {selection}
          <button
            type="button"
            onClick={onClear}
            className="rounded-sm text-gray-400 hover:text-gray-700"
            aria-label={`Clear ${label} filter`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      )}
    </div>
  );
}

export interface ColumnFilterOption {
  value: string;
  label: string;
}

interface ColumnFilterProps {
  label: string;
  options: ColumnFilterOption[];
  /** Chosen option value, or '' for none. */
  value: string;
  onChange: (value: string) => void;
}

/** Column header with a dropdown of a few values (e.g. Alive / Deceased), styled like the form dropdowns. */
export function ColumnFilter({ label, options, value, onChange }: ColumnFilterProps) {
  const selected = options.find((option) => option.value === value);

  return (
    <FilterHeader
      label={label}
      selection={selected?.label}
      onClear={() => onChange('')}
      control={
        // key resets the Select when cleared, so the same option can be picked again.
        <Select key={value || 'none'} value={value || undefined} onValueChange={onChange}>
          <SelectPlainTrigger asChild>
            <FilterIconButton active={Boolean(selected)} label={label} />
          </SelectPlainTrigger>
          <SelectContent align="start" className="min-w-[160px]">
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
    />
  );
}

/** A From / To month range as 'YYYY-MM' ('' = not picked). */
export interface MonthRange {
  from: string;
  to: string;
}

export const EMPTY_MONTH_RANGE: MonthRange = { from: '', to: '' };

export interface MonthRangeFilterProps {
  range: MonthRange;
  onChange: (range: MonthRange) => void;
}

/** Column header with a From / To month picker (e.g. Joined, Given On). */
export function MonthRangeFilter({ label, range, onChange }: MonthRangeFilterProps & { label: string }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<'from' | 'to'>('from');
  const hasRange = Boolean(range.from || range.to);

  const pick = (month: string) => {
    if (editing === 'from') {
      // Keep To on or after From.
      onChange({ from: month, to: range.to && range.to < month ? month : range.to });
      setEditing('to');
      return;
    }
    onChange({ from: range.from && range.from > month ? month : range.from, to: month });
    setOpen(false);
  };

  return (
    <FilterHeader
      label={label}
      selection={hasRange ? `${formatMonthKey(range.from)} – ${formatMonthKey(range.to)}` : ''}
      onClear={() => onChange(EMPTY_MONTH_RANGE)}
      control={
        <Popover
          open={open}
          onOpenChange={(next) => {
            if (next) setEditing('from');
            setOpen(next);
          }}
        >
          <PopoverTrigger asChild>
            <FilterIconButton active={hasRange} label={label} />
          </PopoverTrigger>
          <PopoverContent align="start" sideOffset={6} className="z-[100] w-auto rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
            <div className="mb-3 grid grid-cols-2 gap-2">
              {(['from', 'to'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setEditing(tab)}
                  className={cn(
                    'flex flex-col items-start rounded-lg border p-2 text-left transition-colors',
                    editing === tab ? 'border-[#4369a0] bg-[#4369a0]/5 ring-1 ring-[#4369a0]/30' : 'border-gray-200 bg-white hover:bg-gray-50'
                  )}
                >
                  <span className="text-[10px] font-semibold uppercase text-gray-500">{tab === 'from' ? 'From' : 'To'}</span>
                  <span className="text-xs font-bold text-gray-900">{formatMonthKey(range[tab])}</span>
                </button>
              ))}
            </div>
            <MonthPickerPanel key={editing} value={range[editing]} onSelect={pick} />
            {hasRange && (
              <div className="mt-3 flex justify-end border-t border-gray-100 pt-2.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-[#4369a0]"
                  onClick={() => {
                    onChange(EMPTY_MONTH_RANGE);
                    setEditing('from');
                  }}
                >
                  Clear
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      }
    />
  );
}
